/**
 * 이메일 인증 페이지
 * 
 * @description
 * 사용자가 이메일 인증 코드를 발송받고 입력하여 이메일을 인증하는 페이지입니다.
 * 
 * @features
 * - 이메일 인증 코드 발송
 * - 6자리 인증 코드 입력
 * - 인증 코드 검증
 * - 재발송 기능 (1분 대기)
 * - 인증 완료 후 완료 페이지로 이동
 * 
 * @data-source
 * - 이메일: AuthContext의 user.email 또는 URL 쿼리
 * 
 * @api
 * - POST /auth/send-verification-email (이메일 인증 코드 발송)
 * - POST /auth/verify-verification-code (이메일 인증 코드 검증)
 * 
 * @routes
 * - /verify-email (현재 페이지)
 * - /verify-email/complete (인증 완료 페이지)
 * - /account-deletion/confirm (회원 탈퇴 모드일 경우)
 */

import React, { useState, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input, AlertDialog } from '@/components/shared/ui';
import { ROUTES } from '@/lib/constants/routes';
import { useAuth as useAuthApi } from '@/data/queries/useAuth';
import { isValidEmailFormat, normalizeEmailForAuth } from '@/utils/emailValidation';

// Logo is now in public folder
const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

export default function EmailVerification(): React.ReactElement {
	const { t } = useTranslation('auth');
	// ========== API Hooks ==========
	const { checkEmail, sendVerificationEmail, verifyVerificationCode, sendDeletionCode, verifyDeletionCode, refreshAccessToken } = useAuthApi();
	
	// ========== Hooks ==========
	
    const { user, logout } = useAuth();
    const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const isDeletionMode = searchParams.get('mode') === 'deletion';
	const deletionReason = (location.state as { reason?: string } | null)?.reason;
	/** 이메일 인증 API는 Bearer 필수 — 비로그인 시 발송/검증 불가 */
	const needsLoginForEmailVerify = !isDeletionMode && !user?.isAuthenticated;

	// ========== State 관리 ==========
	
	/**
	 * 이메일 주소
	 * @description 기존 AuthContext의 user.email을 초기값으로 사용 (기존 데이터 소스 우선 사용 규칙)
	 */
    const [email, setEmail] = useState<string>(user?.email || '');
	
	/**
	 * 인증 코드 (6자리)
	 */
    const [code, setCode] = useState<string>('');
	
	/**
	 * 인증 코드 발송 진행 중 상태
	 * @description 인증 코드 발송 API 호출 중 버튼 비활성화
	 */
	const [isSending, setIsSending] = useState<boolean>(false);
	
	/**
	 * 인증 코드 검증 진행 중 상태
	 * @description 인증 코드 검증 API 호출 중 버튼 비활성화
	 */
	const [isVerifying, setIsVerifying] = useState<boolean>(false);
	
	/**
	 * 재발송 대기 시간 (초)
	 * @description 재발송 버튼 비활성화 시간 (1분 = 60초)
	 */
	const [resendCooldown, setResendCooldown] = useState<number>(0);

	/** 마지막으로 인증번호를 발송한 이메일(정규화). 검증 시 동일 주소인지 비교 */
	const [sentToEmailNormalized, setSentToEmailNormalized] = useState<string | null>(null);

	/** 이메일 필드 인라인 오류 (블러·제출 시) */
	const [emailFieldError, setEmailFieldError] = useState<string>('');
	
	/**
	 * AlertDialog 상태
	 * @field isOpen - 팝업 열림 여부
	 * @field message - 표시할 메시지
	 */
	const [alertState, setAlertState] = useState<{ isOpen: boolean; message: string; }>({
		isOpen: false,
		message: '',
	});

	// ========== Refs ==========
	
	/**
	 * 재발송 타이머 ref
	 * @description 컴포넌트 언마운트 시 타이머 정리
	 */
	const resendTimerRef = useRef<NodeJS.Timeout | null>(null);

	// ========== Helper 함수 ==========
	
	/**
	 * AlertDialog 표시 함수
	 * @param message - 표시할 메시지
	 */
	const showAlert = useCallback((message: string) => {
		setAlertState({ isOpen: true, message });
	}, []);

	/**
	 * AlertDialog 닫기 함수
	 */
	const closeAlert = useCallback(() => {
		setAlertState({ isOpen: false, message: '' });
	}, []);

	/**
	 * 재발송 대기 시간 설정
	 * @description 1분(60초) 대기 시간을 설정하고 카운트다운 시작
	 */
	const startResendCooldown = useCallback(() => {
		setResendCooldown(60);
		
		// 기존 타이머 정리
		if (resendTimerRef.current) {
			clearInterval(resendTimerRef.current);
		}
		
		// 1초마다 카운트다운
		resendTimerRef.current = setInterval(() => {
			setResendCooldown((prev) => {
				if (prev <= 1) {
					if (resendTimerRef.current) {
						clearInterval(resendTimerRef.current);
						resendTimerRef.current = null;
					}
					return 0;
				}
				return prev - 1;
			});
		}, 1000);
	}, []);

	// ========== 이메일 인증 코드 발송 ==========
	
	/**
	 * 이메일 인증 코드 발송 핸들러
	 * 
	 * @description
	 * 사용자가 입력한 이메일로 인증 코드를 발송하는 함수
	 * 
	 * @validation
	 * 1. 이메일 입력 여부 확인 → "이메일을 입력해주세요."
	 * 2. 이메일 형식 확인 → "올바른 이메일 형식을 입력해주세요."
	 * 
	 * @api
	 * - POST /auth/send-verification-email
	 * - 서버에서 생성한 코드를 이메일로 전송
	 */
	const validateEmailField = useCallback((): string | null => {
		const trimmed = email.trim();
		if (!trimmed) {
			return t('emailVerification.alertEmailRequired');
		}
		if (!isValidEmailFormat(trimmed)) {
			return t('emailVerification.alertEmailInvalid');
		}
		return null;
	}, [email, t]);

	const handleSendCode = async () => {
		if (needsLoginForEmailVerify) {
			showAlert(t('emailVerification.alertLoginRequired'));
			return;
		}
		// ========== 1단계: 유효성 검사 ==========
		const emailErr = validateEmailField();
		if (emailErr) {
			setEmailFieldError(emailErr);
			showAlert(emailErr);
			return;
		}
		setEmailFieldError('');
		const emailNormalized = normalizeEmailForAuth(email);

		// ========== 2단계: 인증 코드 발송 API 호출 ==========
		
		setIsSending(true);

		try {
			if (isDeletionMode) {
				// 회원 탈퇴 인증 코드 발송
				await sendDeletionCode();
			} else {
				const check = await checkEmail(emailNormalized);
				if (!check.isAvailable) {
					showAlert(check.message || t('emailVerification.alertEmailInUse'));
					return;
				}
				// 이메일 인증 코드 발송 (서버·메일과 동일하게 정규화된 주소 사용)
				await sendVerificationEmail(emailNormalized);
				setSentToEmailNormalized(emailNormalized);
			}

		// 성공 메시지 표시
		showAlert(t('emailVerification.alertCodeSent'));
		
		// 재발송 대기 시간 설정 (1분)
		startResendCooldown();
		} catch (error: any) {
			showAlert(error.message || t('emailVerification.alertCodeSendError'));
		} finally {
			// 항상 실행: 로딩 상태 종료
			setIsSending(false);
		}
	};

	// ========== 이메일 인증 코드 검증 ==========
	
	/**
	 * 이메일 인증 코드 검증 핸들러
	 * 
	 * @description
	 * 사용자가 입력한 인증 코드를 검증하는 함수
	 * 
	 * @param e - 폼 제출 이벤트
	 * 
	 * @validation
	 * 1. 이메일 입력 여부 확인 → "이메일을 입력해주세요."
	 * 2. 인증 코드 입력 여부 확인 → "인증 코드를 입력해주세요."
	 * 3. 인증 코드 길이 확인 (6자리) → "인증 코드는 6자리입니다."
	 * 
	 * @api
	 * - POST /auth/verify-verification-code
	 * - 성공 시 완료 페이지로 이동
	 */
	const handleVerify = async (e: React.FormEvent) => {
		e.preventDefault();

		if (needsLoginForEmailVerify) {
			showAlert(t('emailVerification.alertLoginRequired'));
			return;
		}

		// ========== 1단계: 유효성 검사 ==========
		const emailErr = validateEmailField();
		if (emailErr) {
			setEmailFieldError(emailErr);
			showAlert(emailErr);
			return;
		}
		setEmailFieldError('');

		const emailNormalized = normalizeEmailForAuth(email);

		if (!isDeletionMode && sentToEmailNormalized && emailNormalized !== sentToEmailNormalized) {
			showAlert(t('emailVerification.alertEmailMustMatchSent'));
			return;
		}

		// 인증 코드 입력 여부 확인
		if (!code || code.trim() === '') {
			showAlert(t('emailVerification.alertCodeRequired'));
			return;
		}

		// 인증 코드 길이 확인 (6자리)
		if (code.trim().length !== 6) {
			showAlert(t('emailVerification.alertCodeLength'));
			return;
		}

		// ========== 2단계: 인증 코드 검증 API 호출 ==========
		
		setIsVerifying(true);

		try {
			if (isDeletionMode) {
				// 회원 탈퇴 인증 코드 검증
				await verifyDeletionCode(code.trim());
			} else {
				// 이메일 인증 코드 검증 (발송 시와 동일한 정규화 주소)
				await verifyVerificationCode(emailNormalized, code.trim());
				
				// 이메일 인증 성공 시 토큰 갱신 (role 업데이트 반영)
				try {
					const storedRefreshToken = localStorage.getItem('medicrew_refresh_token');
					if (storedRefreshToken) {
						const tokenResponse = await refreshAccessToken(storedRefreshToken);
						if (tokenResponse?.accessToken) {
							localStorage.setItem('medicrew_access_token', tokenResponse.accessToken);
						}
					}
				} catch (tokenError: any) {
					// refresh token 만료 시 로그아웃하고 로그인 페이지로 이동
					// HTTP 상태 코드로 판단 (401 Unauthorized)
					if (tokenError?.status === 401) {
						logout();
						showAlert(t('emailVerification.alertTokenExpired', { defaultValue: '세션이 만료되었습니다. 다시 로그인해주세요.' }));
						setTimeout(() => {
							navigate(ROUTES.AUTH.LOGIN);
						}, 1500);
						return;
					}
					// 기타 토큰 갱신 오류는 로그만 남기고 계속 진행 (Settings에서 프로필 조회 시 다시 시도)
					console.error('토큰 갱신 실패:', tokenError);
				}
			}

	// 성공 메시지 표시
	const successMessage = isDeletionMode 
		? t('emailVerification.successGuest')
		: t('emailVerification.successFull');
	showAlert(successMessage);
	
	// 잠시 후 완료 페이지로 이동
	setTimeout(() => {
		if (isDeletionMode) {
			navigate(ROUTES.AUTH.ACCOUNT_DELETION_CONFIRM, { state: { reason: deletionReason } });
		} else {
			// 이메일 인증 완료 후 Settings 페이지로 이동 (프로필 자동 갱신)
			navigate(ROUTES.AUTH.SETTINGS);
		}
	}, 1500);
		} catch (error: any) {
			showAlert(
				typeof error?.message === 'string' && error.message.trim()
					? error.message
					: t('emailVerification.alertVerifyError')
			);
		} finally {
			// 항상 실행: 로딩 상태 종료
			setIsVerifying(false);
		}
	};

	// ========== Cleanup ==========
	
	/**
	 * 컴포넌트 언마운트 시 타이머 정리
	 */
	React.useEffect(() => {
		return () => {
			if (resendTimerRef.current) {
				clearInterval(resendTimerRef.current);
			}
		};
	}, []);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-8">
            {/* Verification Card */}
            <div className="w-full max-w-[460px] bg-white rounded-3xl p-14 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100">
                {/* Logo */}
                <div className="text-center mb-10">
                    <Link to="/" className="inline-block">
                        <img src={medicrewLogo} alt="Medicrew" className="h-7 w-auto" />
                    </Link>
                </div>

                {/* Title */}
                <h1 className="text-2xl font-semibold text-brand-500 text-center mb-8">
                    {t('emailVerification.title')}
                </h1>

				{needsLoginForEmailVerify && (
					<div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
						<p className="mb-2">{t('emailVerification.loginRequiredBanner')}</p>
						<Link to={ROUTES.AUTH.LOGIN} className="font-semibold text-brand-600 underline hover:text-brand-700">
							{t('emailVerification.loginRequiredLink')}
						</Link>
					</div>
				)}

                {/* Form */}
                <form onSubmit={handleVerify} className="flex flex-col gap-6">
                    <div>
                        <label htmlFor="email" className="block mb-2 text-sm font-semibold text-gray-900">
                            {t('emailVerification.emailLabel')}
                        </label>
                        <Input
                            type="email"
                            id="email"
                            autoComplete="email"
                            className={`bg-gray-50 focus:bg-white border-transparent focus:border-brand-500 rounded-xl ${emailFieldError ? 'border-red-500 focus:border-red-500' : ''}`}
                            placeholder="example@email.com"
                            value={email}
                            onChange={(e) => {
								setEmail(e.target.value);
								if (emailFieldError) setEmailFieldError('');
							}}
                            onBlur={() => {
								const err = validateEmailField();
								setEmailFieldError(err ?? '');
							}}
                            required
                        />
						{emailFieldError ? (
							<p className="mt-1.5 text-xs font-medium text-red-600">{emailFieldError}</p>
						) : (
							<p className="mt-1.5 text-xs text-gray-500">{t('emailVerification.emailHint')}</p>
						)}
                    </div>

                    <div>
                        <label htmlFor="code" className="block mb-2 text-sm font-semibold text-gray-900">
                            {t('emailVerification.codeLabel')}
                        </label>
                        <div className="flex gap-3">
                            <Input
                                type="text"
                                id="code"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                autoComplete="one-time-code"
                                className="flex-1 bg-gray-50 focus:bg-white border-transparent focus:border-brand-500 rounded-xl"
                                placeholder={t('emailVerification.codePlaceholder')}
                                maxLength={6}
                                value={code}
                                onChange={(e) => {
									const v = e.target.value.replace(/\D/g, '').slice(0, 6);
									setCode(v);
								}}
                                required
                            />
                            <Button
                                type="button"
                                onClick={handleSendCode}
                                variant="secondary"
                                className="shrink-0 px-6 border-2 border-brand-500 text-brand-500 rounded-xl h-10"
								disabled={isSending || resendCooldown > 0}
                            >
                                {isSending 
									? t('emailVerification.sending') 
									: resendCooldown > 0 
										? t('emailVerification.resendButton', { seconds: resendCooldown })
										: t('emailVerification.sendButton')
								}
                            </Button>
                        </div>
                    </div>

                    <div className="flex flex-col items-center gap-4">
                        <Button
                            type="submit"
                            className="w-full rounded-full text-lg h-12 shadow-md"
                            variant="primary"
							disabled={isVerifying}
                        >
                            {isVerifying ? t('emailVerification.verifying') : t('emailVerification.verifyButton')}
                        </Button>
                    </div>
                </form>

                {/* Help Section */}
                <div className="mt-8 p-4 bg-gray-50 rounded-2xl">
                    <h2 className="text-sm font-bold text-gray-900 mb-4">
                        {t('emailVerification.helpTitle')}
                    </h2>
                    <ul className="text-xs text-gray-500 space-y-2 list-none p-0">
                        <li className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-gray-400">
                            {t('emailVerification.noteDelay')}
                        </li>
                        <li className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-gray-400">
                            {t('emailVerification.noteSpam')}
                        </li>
                        <li className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-gray-400">
                            {t('emailVerification.noteFirewall')}
                        </li>
                    </ul>
                </div>

                <div className="mt-4">
                    <Link
                        to={ROUTES.HOME}
                        className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                    >
                        ← {t('homeLink')}
                    </Link>
                </div>
            </div>


			{/* AlertDialog: 인증 코드 발송/검증 성공/실패 메시지 표시 */}
			<AlertDialog
				isOpen={alertState.isOpen}
				onClose={closeAlert}
				message={alertState.message}
			/>
        </div>
    );
}
